import React, { useState, useEffect, useRef } from "react";
import {
  GlobalStyle,
  PaobcTheme,
  breakpoints,
} from "../../components/common/mixins";
import paoHeroBg from "../../assets/images/pao-hero-bg.webp";
import paoHeroBgMobile from "../../assets/images/pao-hero-bg-mobile.webp";
import paoHeroText from "../../assets/images/pao-hero-text.svg";
import paoHeroTextMobile from "../../assets/images/pao-hero-text-mobile.svg";
import { Warning } from "../../components/icons/Warning";
import { Container } from "../../components/common/Container";
import { CenteredOverlay } from "../../components/common/CenteredOverlay";
import { EventCard, EventProps } from "../../components/common/EventCard";
import { InfoModal } from "../reservationPage/InfoModal";
import { PaobcPageSkeleton } from "./paobc-page-skeleton";
import { apiGet } from "../../api/fetch";
import { endpoints } from "../../utils/Constants";
import { EventCategory } from "../../common/enums/EventCategory";
import { getCountry, getLang, sitePolicyState } from "../../utils/Utils";
import useModal from "../../hooks/useModal";
import SitePolicyPrompt from "../../components/SitePolicyPrompt.jsx";
import {
  PaobcScopedGlobalStyle,
  ContentSection,
  HeroBanner,
  HeroBannerMedia,
  HeroBannerImage,
  HeroBannerText,
  Section,
  EventsGrid,
  EventsContainer,
  EventsTitle,
  EmptyState,
  EmptyStateText,
} from "./paobc-page.styles";

const PAOBC_PRODUCER_ID = 64;

type PaobcResources = {
  AvailableEvents: string;
  SportsSeason: string;
  NoEventsAvailable: string;
  SomethingWrong: string;
  SomethingWrongText: string;
  TryAgain: string;
  SoldOut: string;
};

export function PaobcApp() {
  const [events, setEvents] = useState<EventProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const {
    isOpen: isErrorOpen,
    openModal: openError,
    closeModal: closeError,
  } = useModal(false, { disableScrollLock: true });
  const sitePolicyPromptRef = useRef<{
    open: (reason?: string, url?: string) => void;
  }>(null);

  const resources = window.homepage_resources as unknown as PaobcResources;

  const handleCardClick =
    (event: EventProps) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.categoryId === EventCategory.Sports && sitePolicyState()) {
        e.preventDefault();
        sitePolicyPromptRef.current?.open(undefined, event.redirectUrl);
      }
    };

  useEffect(() => {
    const country = getCountry();
    const lang = getLang();
    apiGet(
      endpoints.events.getEventsByProducer(
        country,
        lang,
        PAOBC_PRODUCER_ID,
        EventCategory.Sports,
      ),
    )
      .then(({ data }) => {
        const localePrefix = `/${country}-${lang}`;
        const sorted = (data ?? [])
          .sort(
            (a: EventProps, b: EventProps) =>
              Number(b.isSeasonTicket) - Number(a.isSeasonTicket),
          )
          .map((event: EventProps) => ({
            ...event,
            redirectUrl:
              event.redirectUrl &&
              !/^\/[a-z]{2}-[a-z]{2}\//.test(event.redirectUrl)
                ? `${localePrefix}/tickets${event.redirectUrl}`
                : event.redirectUrl,
          }));
        setEvents(sorted);
      })
      .catch((error) => {
        console.error("Failed to fetch PAOBC events", error);
        openError();
      })
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      <PaobcTheme />
      <GlobalStyle />
      <PaobcScopedGlobalStyle />

      <ContentSection>
        <div>
          {/* Banner Section */}
          <HeroBanner>
            <HeroBannerMedia>
              <picture>
                <source
                  media={`(max-width: ${breakpoints.mobile - 1}px)`}
                  srcSet={paoHeroBgMobile}
                />
                <HeroBannerImage
                  src={paoHeroBg}
                  alt="banner"
                  fetchpriority="high"
                />
              </picture>
              <picture>
                <source
                  media={`(max-width: ${breakpoints.mobile - 1}px)`}
                  srcSet={paoHeroTextMobile}
                />
                <HeroBannerText
                  src={paoHeroText}
                  alt="Panathinaikos BC Aktor"
                  fetchpriority="high"
                />
              </picture>
            </HeroBannerMedia>
          </HeroBanner>

          {/* Events Section */}
          {isLoading ? (
            <PaobcPageSkeleton />
          ) : (
            <Container>
              <Section>
                <EventsContainer>
                  {events.length > 0 && (
                    <EventsTitle>{resources.AvailableEvents}</EventsTitle>
                  )}
                  {events.length > 0 ? (
                    <EventsGrid>
                      {events.map((event) => (
                        <EventCard
                          key={event.eventGroupId}
                          {...event}
                          dateLabel={
                            event.isSeasonTicket
                              ? resources.SportsSeason
                              : undefined
                          }
                          labels={{ soldOut: resources.SoldOut }}
                          onClick={handleCardClick(event)}
                        />
                      ))}
                    </EventsGrid>
                  ) : (
                    <EmptyState>
                      <EmptyStateText>
                        {resources.NoEventsAvailable}
                      </EmptyStateText>
                    </EmptyState>
                  )}
                </EventsContainer>
              </Section>
            </Container>
          )}
        </div>
      </ContentSection>

      <SitePolicyPrompt
        intent="view"
        mandatory={false}
        autoOpen={false}
        ref={sitePolicyPromptRef}
      />

      <CenteredOverlay
        open={isErrorOpen}
        handleClose={closeError}
        id="paobc-error"
        titleText={resources.SomethingWrong}
        icon={<Warning color="#FC2D33" />}
        size="sm"
      >
        <InfoModal
          description={resources.SomethingWrongText}
          buttonText={resources.TryAgain}
          onClick={closeError}
        />
      </CenteredOverlay>
    </>
  );
}
